import { useEffect, useRef } from 'react';
import type { Analytics } from './client';
import type { GetToken } from '../../../shared/sgapp-subscription';

/**
 * A signed-in visitor, as the page knows them. Clerk is the source; `null` means nobody
 * is signed in, and `undefined` means we have not been told either way yet (Clerk is
 * still resolving, or there is no Clerk key at all).
 */
export interface Identity {
  id: string;
  email?: string;
}

/**
 * Pushes the Clerk identity into PostHog, once per identity.
 *
 * This exists because a flag condition on a person property cannot evaluate against an
 * anonymous visitor. The page had Clerk identity and never passed it on, so every
 * targeted flag on preview.240.org silently matched nobody.
 *
 * Note the distinct_id is Clerk's user id. sgapp-client identifies with its own user id
 * (src/App.tsx), so the same human is two PostHog persons across the two apps. Flag
 * conditions on `email` work in both regardless, which is what this is for — unifying
 * the two ids is a bigger decision than this page should make on its own.
 */
export function useIdentity(analytics: Analytics | null, identity: Identity | null | undefined, getToken?: GetToken): void {
  // What we last told PostHog, so a re-render or a parent re-mount does not re-identify.
  const sent = useRef<string | null>(null);

  useEffect(() => {
    if (!analytics) return;

    // `undefined` is "not known yet" and must not be read as a sign-out.
    if (identity === undefined) {
      analytics.setSubscriptionIdentity?.(null);
      return;
    }

    if (identity === null) {
      analytics.setSubscriptionIdentity?.(null);
      analytics.markAnonymous?.();
      if (sent.current !== null) {
        analytics.reset?.();
        sent.current = null;
      }
      return;
    }

    if (sent.current === identity.id) {
      analytics.setSubscriptionIdentity?.(identity.id, getToken);
      return;
    }
    // A different person on the same browser: drop the old identity before claiming
    // the new one, or PostHog merges the two into one person.
    if (sent.current !== null) analytics.reset?.();

    analytics.identify?.(identity.id, { email: identity.email });
    analytics.setSubscriptionIdentity?.(identity.id, getToken);
    sent.current = identity.id;
  }, [analytics, identity, getToken]);
}
